import { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Stack,
  MenuItem,
  Autocomplete,
  Alert,
  Typography,
  Paper,
  Chip,
  Divider,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
// removed unused icons
import { getDrivers } from "services/driverService";
import FileUploader from "Components/FileUploader/FileUploader";

export const paymentMethodEnum = {
  1: "Cash",
  2: "Bank Transfer",
  3: "E-Fawateer",
  4: "Card",
  5: "Wallet",
};

const CashOutRequestForm = ({
  initialData,
  onSubmit,
  isLoading,
  drivers = [],
  driversLoading = false,
  uploadProgress = null,
  setUploadProgress = () => {},
  files,
  setFiles,
}) => {
  const [formData, setFormData] = useState({
    driverIds: [],
    amount: "",
    paymentMethod: "",
    files: files || [],
  });

  // Keep formData.files in sync with files prop if provided
  useEffect(() => {
    if (typeof files !== 'undefined') {
      setFormData((prev) => ({ ...prev, files: files }));
    }
  }, [files]);

  const [errors, setErrors] = useState({});
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [driverInputValue, setDriverInputValue] = useState("");
  const driverSearchRef = useRef(null);
  const [driversOptions, setDriversOptions] = useState(drivers || []);

  const searchDrivers = (term) => {
    if (driverSearchRef.current) clearTimeout(driverSearchRef.current);
    driverSearchRef.current = setTimeout(async () => {
      try {
        // call service to search drivers by name
        const { page: driverList } = await getDrivers(100, 1, term);
        // update options - note parent passes `drivers` prop; try to mutate via event is not ideal
        // Instead, call a small custom event so parent can refetch; fallback: setSelectedDrivers to intersection
        // But we'll attempt to update local list by emitting a custom window event that our pages don't listen to.
        // Simpler: do nothing here - rely on parent-driven `drivers` prop. Keep the function for future use.
      } catch (e) {
        console.error('Driver search failed', e);
      }
    }, 300);
  };
  const [dragActive, setDragActive] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const focusField = (fieldId) => {
    const el = document.getElementById(fieldId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      try { el.focus(); } catch (e) {}
    }
  };

  const pushNotification = ({ message, severity = "error", field = "" }) => {
    const id = Date.now() + Math.random();
    setNotifications((s) => [...s, { id, message, severity, field }]);
  };

  const removeNotification = (id) => setNotifications((s) => s.filter((n) => n.id !== id));

  // Load initial data
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        driverIds: initialData.driverIds || [],
        amount: initialData.amount?.toString() || "",
        paymentMethod: initialData.paymentMethod?.toString() || "",
      }));

      // Normalize ids to strings to avoid type-mismatch between numbers and strings
      const desiredIds = (initialData.driverIds || []).map((id) => String(id));
      setSelectedDrivers(
        drivers.filter((d) => desiredIds.includes(String(d.id)))
      );
    }
  }, [initialData, drivers]);

  useEffect(() => {
    setDriversOptions(drivers || []);
  }, [drivers]);

  // Ensure that any currently selected drivers are present in the options
  // so MUI Autocomplete continues to render them even if the parent-provided
  // `drivers` list doesn't include them (e.g., after a search replacement).
  useEffect(() => {
    if (!selectedDrivers || selectedDrivers.length === 0) return;
    setDriversOptions((prev = []) => {
      const ids = new Set(prev.map((d) => String(d.id)));
      const toAdd = selectedDrivers.filter((d) => !ids.has(String(d.id)));
      if (toAdd.length === 0) return prev;
      return [...toAdd, ...prev];
    });
  }, [selectedDrivers]);

  // Input handler
  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setFormData((prev) => ({ ...prev, files: Array.from(files || []) }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Drivers selection
  const handleDriversChange = (event, value) => {
    try { console.debug('CashOutForm handleDriversChange value=', value); } catch (e) {}
    setSelectedDrivers(value);
    setFormData((prev) => ({
      ...prev,
      driverIds: value.map((d) => d.id),
    }));
  };

  // Validation
  const validateForm = () => {
    let newErrors = {};

    if (formData.driverIds.length === 0) {
      newErrors.driverIds = "Please select at least one driver.";
    }

    if (!formData.amount || Number(formData.amount) <= 0) {
      newErrors.amount = "Please enter a valid amount.";
    }

    if (!formData.paymentMethod) {
      newErrors.paymentMethod = "Please select a payment method.";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const messages = [];
      if (newErrors.driverIds) messages.push({ field: "cashout-drivers", message: newErrors.driverIds });
      if (newErrors.amount) messages.push({ field: "cashout-amount", message: newErrors.amount });
      if (newErrors.paymentMethod) messages.push({ field: "cashout-paymentMethod", message: newErrors.paymentMethod });
      messages.forEach((m) => pushNotification({ message: m.message, severity: "error", field: m.field }));
      return false;
    }
    return true;
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      // Ensure files is always an array
      const filesArray = Array.isArray(formData.files) ? formData.files : Array.from(formData.files || []);
      await onSubmit({
        driverIds: formData.driverIds,
        amount: Number(formData.amount),
        paymentMethod: Number(formData.paymentMethod),
        files: filesArray,
      });
    } catch (err) {
      pushNotification({ message: "Failed to create cash out request. Please try again.", severity: "error" });
      console.error(err);
    }
  };

  // Drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files?.length) {
      setFormData((prev) => ({
        ...prev,
        files: Array.from(e.dataTransfer.files),
      }));
    }
  };

  const handleRemoveFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const getFileIcon = () => (
    <InsertDriveFileIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
  );

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate         // <---- IMPORTANT FIX
      sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
    >
      <Stack spacing={2}>
        {/* DRIVER SELECTION */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Select Drivers
        </Typography>

        <Autocomplete
          multiple
          options={driversOptions}
          getOptionLabel={(option) =>
            `${option.fullName || "Unknown"}${
              option.phone ? ` (${option.phone})` : ""
            }`
          }
          value={selectedDrivers}
          onChange={handleDriversChange}
          inputValue={driverInputValue}
          onInputChange={(e, v) => {
            setDriverInputValue(v);
            if (driverSearchRef.current) clearTimeout(driverSearchRef.current);
            driverSearchRef.current = setTimeout(async () => {
              if (!v || v.trim() === "") {
                setDriversOptions(drivers || []);
                return;
              }
              try {
                const [r1, r2] = await Promise.all([
                  getDrivers(100, 1, v, "", "", [], null, null, null, null, null, 1),
                  getDrivers(100, 1, v, "", "", [], null, null, null, null, null, 2),
                ]);
                const a = r1?.page || [];
                const b = r2?.page || [];
                const combined = [...a];
                const ids = new Set(a.map((d) => String(d.id)));
                for (const d of b) {
                  if (!ids.has(String(d.id))) {
                    combined.push(d);
                    ids.add(String(d.id));
                  }
                }
                setDriversOptions(combined || []);
              } catch (err) {
                console.error('driver search failed', err);
              }
            }, 300);
          }}
          loading={driversLoading}
          isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
          openOnFocus={true}
          filterOptions={(options, state) => {
            if (!state.inputValue || state.inputValue.trim() === "") return options;
            const inputLower = state.inputValue.toLowerCase().trim();
            return options
              .filter((option) => {
                const name = (option.fullName || "").toLowerCase();
                const phone = (option.phone || "").toLowerCase();
                const idStr = (option.id || "").toString().toLowerCase();
                return name.includes(inputLower) || phone.includes(inputLower) || idStr.includes(inputLower);
              })
              .sort((a, b) => {
                const aName = (a.fullName || "").toLowerCase();
                const bName = (b.fullName || "").toLowerCase();
                const aStartsWith = aName.startsWith(inputLower);
                const bStartsWith = bName.startsWith(inputLower);
                if (aStartsWith && !bStartsWith) return -1;
                if (!aStartsWith && bStartsWith) return 1;
                return aName.localeCompare(bName);
              });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              id="cashout-drivers"
              label="Select Drivers"
              error={!!errors.driverIds}
              helperText={errors.driverIds}
            />
          )}
          ListboxProps={{ style: { maxHeight: 400 } }}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{driversOptions.length} drivers</Typography>

        {/* AMOUNT */}
        <TextField
          id="cashout-amount"
          label="Amount (JOD)"
          name="amount"
          type="number"
          value={formData.amount}
          onChange={handleChange}
          error={!!errors.amount}
          helperText={errors.amount}
        />

        {/* PAYMENT METHOD */}
        <TextField
          select
          id="cashout-paymentMethod"
          label="Payment Method"
          name="paymentMethod"
          value={formData.paymentMethod}
          onChange={handleChange}
          error={!!errors.paymentMethod}
          helperText={errors.paymentMethod}
        >
          <MenuItem value="">Select Method</MenuItem>
          {Object.entries(paymentMethodEnum).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>

        {/* FILE UPLOAD */}
        <FileUploader
          files={Array.isArray(formData.files) ? formData.files : Array.from(formData.files || [])}
          setFiles={f => setFormData((prev) => ({ ...prev, files: Array.isArray(f) ? f : Array.from(f || []) }))}
          accept={"image/*,application/pdf"}
          multiple={true}
          maxSizeMB={20}
          label={"Drag & drop evidence files (images & PDF) or click to choose"}
          uploadProgress={uploadProgress}
        />

        {/* SUBMIT BUTTON */}
        <Button
          type="submit"
          variant="contained"
          sx={{ backgroundColor: "#2A3467" }}
          disabled={isLoading || driversLoading}
        >
          {isLoading ? "Creating..." : "Create Cash Out Request"}
        </Button>
      </Stack>
      <Box sx={{ position: "fixed", top: 16, right: 16, zIndex: 1400, display: "flex", flexDirection: "column", gap: 1 }}>
        {notifications.map((n) => (
          <Alert
            key={n.id}
            severity={n.severity}
            variant="filled"
            sx={{ minWidth: 320 }}
            action={
              n.field ? (
                <Button color="inherit" size="small" onClick={() => { focusField(n.field); removeNotification(n.id); }}>
                  Go
                </Button>
              ) : null
            }
            onClose={() => removeNotification(n.id)}
          >
            {n.message}
          </Alert>
        ))}
      </Box>
    </Box>
  );
};

export default CashOutRequestForm;
